import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { InteractiveNvlWrapper } from '@neo4j-nvl/react'
import { api, type KgBrowserNode, type KgBrowserRelationship } from '@/lib/api'

// Minimal NVL types (avoiding direct @neo4j-nvl/base import for pnpm strict mode)
interface NvlNode {
  id: string
  caption?: string
  color?: string
  size?: number
  selected?: boolean
  pinned?: boolean
}

interface NvlRelationship {
  id: string
  from: string
  to: string
  caption?: string
  color?: string
  width?: number
}
import { KG_NODE_STYLES, KG_RELATIONSHIP_COLORS, DEFAULT_RELATIONSHIP_COLOR, DEFAULT_NODE_STYLE } from './kg-constants'
import { KgToolbar } from './KgToolbar'
import { KgNodeDetailPanel } from './KgNodeDetailPanel'
import { KgSelectionPanel } from './KgSelectionPanel'
import { KgLegend } from './KgLegend'
import { KgXyflowView } from './KgXyflowView'

interface TooltipState {
  visible: boolean
  x: number
  y: number
  label: string
  caption: string
  properties: Record<string, unknown>
}

export interface SelectedNodeInfo {
  id: string
  label: string
  caption: string
  properties: Record<string, unknown>
}

interface KgVisualizationProps {
  scope: 'portfolio' | 'customer' | 'territory' | 'ae'
  entityIds?: string[]
  height?: string
  onSelectionChange?: (nodes: SelectedNodeInfo[]) => void
  onAskAboutSelection?: (context: string) => void
}

export function KgVisualization({ scope, entityIds = [], height = '600px', onSelectionChange, onAskAboutSelection }: KgVisualizationProps) {
  // Stable key for entityIds to use in dependency arrays
  const entityIdsKey = entityIds.join(',')

  const [layout, setLayout] = useState<'forceDirected' | 'hierarchical' | 'radial'>('forceDirected')
  const [depth, setDepth] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, label: '', caption: '', properties: {} })

  // Track all nodes and relationships (initial + expanded)
  const [extraNodes, setExtraNodes] = useState<KgBrowserNode[]>([])
  const [extraRels, setExtraRels] = useState<KgBrowserRelationship[]>([])

  const nvlRef = useRef<any>(null)
  const graphContainerRef = useRef<HTMLDivElement>(null)

  // Fetch initial subgraph
  const { data: subgraph, isLoading, error } = useQuery({
    queryKey: ['kg-subgraph', scope, entityIdsKey, depth],
    queryFn: () => api.kg.browser.subgraph(scope, entityIds, depth),
    enabled: scope === 'portfolio' || entityIds.length > 0,
    staleTime: 30000,
  })

  // Reset extras when scope/entity/depth changes
  useEffect(() => {
    setExtraNodes([])
    setExtraRels([])
    setSelectedNodeIds(new Set())
  }, [scope, entityIdsKey, depth])

  // Combine initial + expanded data
  const allKgNodes = useMemo(() => {
    const base = subgraph?.nodes || []
    const map = new Map<string, KgBrowserNode>()
    for (const n of base) map.set(n.id, n)
    for (const n of extraNodes) map.set(n.id, n)
    return Array.from(map.values())
  }, [subgraph?.nodes, extraNodes])

  const allKgRels = useMemo(() => {
    const base = subgraph?.relationships || []
    const map = new Map<string, KgBrowserRelationship>()
    for (const r of base) map.set(r.id, r)
    for (const r of extraRels) map.set(r.id, r)
    return Array.from(map.values())
  }, [subgraph?.relationships, extraRels])

  // Available and visible types
  const availableTypes = useMemo(() => {
    const types = new Set<string>()
    for (const n of allKgNodes) types.add(n.label)
    return Array.from(types)
  }, [allKgNodes])

  const [visibleTypes, setVisibleTypes] = useState<Set<string>>(new Set())

  // Initialize visible types when available types change
  useEffect(() => {
    setVisibleTypes(new Set(availableTypes))
  }, [availableTypes])

  const handleTypeToggle = useCallback((type: string) => {
    setVisibleTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        // Prevent deselecting the last visible type
        if (next.size <= 1) return prev
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }, [])

  // Filter nodes by visible types
  const filteredKgNodes = useMemo(
    () => allKgNodes.filter(n => visibleTypes.has(n.label)),
    [allKgNodes, visibleTypes]
  )

  const filteredNodeIds = useMemo(
    () => new Set(filteredKgNodes.map(n => n.id)),
    [filteredKgNodes]
  )

  const filteredKgRels = useMemo(
    () => allKgRels.filter(r => filteredNodeIds.has(r.from) && filteredNodeIds.has(r.to)),
    [allKgRels, filteredNodeIds]
  )

  // Map to NVL format
  const nvlNodes: NvlNode[] = useMemo(
    () => filteredKgNodes.map(n => {
      const style = KG_NODE_STYLES[n.label] || DEFAULT_NODE_STYLE
      return {
        id: n.id,
        caption: n.caption,
        color: style.color,
        size: style.size,
        selected: selectedNodeIds.has(n.id),
      }
    }),
    [filteredKgNodes, selectedNodeIds]
  )

  const nvlRels: NvlRelationship[] = useMemo(
    () => filteredKgRels.map(r => ({
      id: r.id,
      from: r.from,
      to: r.to,
      caption: r.type.replace(/_/g, ' '),
      color: KG_RELATIONSHIP_COLORS[r.type] || DEFAULT_RELATIONSHIP_COLOR,
      width: 1.5,
    })),
    [filteredKgRels]
  )

  // Build a lookup map from node ID → KG node data (for hover tooltip)
  const kgNodeMap = useMemo(() => {
    const map = new Map<string, KgBrowserNode>()
    for (const n of filteredKgNodes) map.set(n.id, n)
    return map
  }, [filteredKgNodes])

  // Handle node double-click: expand neighborhood
  // NVL callback signature: (node, hitTargets, event)
  const handleNodeDoubleClick = useCallback(async (node: NvlNode) => {
    try {
      const result = await api.kg.browser.expand(node.id, 1)
      setExtraNodes(prev => [...prev, ...result.nodes])
      setExtraRels(prev => [...prev, ...result.relationships])
    } catch (err) {
      console.error('[KG] Expand failed:', err)
    }
  }, [])

  // Notify parent of selection changes
  const notifySelectionChange = useCallback((ids: Set<string>) => {
    if (!onSelectionChange) return
    const nodes: SelectedNodeInfo[] = []
    for (const id of ids) {
      const kgNode = kgNodeMap.get(id)
      if (kgNode) {
        nodes.push({ id: kgNode.id, label: kgNode.label, caption: kgNode.caption, properties: kgNode.properties })
      }
    }
    onSelectionChange(nodes)
  }, [onSelectionChange, kgNodeMap])

  // Handle node click: shift+click for multi-select, plain click for single select
  // NVL callback signature: (node, hitTargets, event)
  const handleNodeClick = useCallback((node: NvlNode, _hitTargets: unknown, event: MouseEvent) => {
    if (event.shiftKey || event.metaKey) {
      // Multi-select: toggle node in/out of selection
      setSelectedNodeIds(prev => {
        const next = new Set(prev)
        if (next.has(node.id)) next.delete(node.id)
        else next.add(node.id)
        notifySelectionChange(next)
        return next
      })
    } else {
      // Single select: replace selection
      setSelectedNodeIds(prev => {
        if (prev.size === 1 && prev.has(node.id)) {
          notifySelectionChange(new Set())
          return new Set()
        }
        const next = new Set([node.id])
        notifySelectionChange(next)
        return next
      })
    }
  }, [notifySelectionChange])

  // Handle hover: show tooltip with node info
  const handleHover = useCallback((element: NvlNode | NvlRelationship | null, _hitTargets: unknown, event: MouseEvent) => {
    if (!element || !('caption' in element)) {
      setTooltip(prev => prev.visible ? { ...prev, visible: false } : prev)
      return
    }

    // Check if it's a node (has no 'from'/'to' fields)
    const isNode = !('from' in element)
    if (!isNode) {
      setTooltip(prev => prev.visible ? { ...prev, visible: false } : prev)
      return
    }

    const kgNode = kgNodeMap.get(element.id)
    const containerRect = graphContainerRef.current?.getBoundingClientRect()
    const x = containerRect ? event.clientX - containerRect.left + 12 : event.clientX + 12
    const y = containerRect ? event.clientY - containerRect.top - 10 : event.clientY - 10

    setTooltip({
      visible: true,
      x,
      y,
      label: kgNode?.label || '',
      caption: kgNode?.caption || element.caption || element.id,
      properties: kgNode?.properties || {},
    })
  }, [kgNodeMap])

  // Search
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return
    try {
      const results = await api.kg.browser.search(searchQuery.trim(), scope, entityIds[0])
      const first = results[0]
      if (first) {
        const next = new Set([first.id])
        setSelectedNodeIds(next)
        notifySelectionChange(next)
        // If node is already in graph, highlight it. Otherwise load it via expand.
        if (!filteredNodeIds.has(first.id)) {
          const result = await api.kg.browser.expand(first.id, 1)
          setExtraNodes(prev => [...prev, ...result.nodes])
          setExtraRels(prev => [...prev, ...result.relationships])
        }
      }
    } catch (err) {
      console.error('[KG] Search failed:', err)
    }
  }, [searchQuery, scope, entityIds, filteredNodeIds, notifySelectionChange])

  // Select node from detail/selection panel
  const handleNodeSelectFromPanel = useCallback((nodeId: string) => {
    const next = new Set([nodeId])
    setSelectedNodeIds(next)
    notifySelectionChange(next)
  }, [notifySelectionChange])

  // Remove a node from multi-selection
  const handleDeselectNode = useCallback((nodeId: string) => {
    setSelectedNodeIds(prev => {
      const next = new Set(prev)
      next.delete(nodeId)
      notifySelectionChange(next)
      return next
    })
  }, [notifySelectionChange])

  // Clear all selection
  const handleClearSelection = useCallback(() => {
    setSelectedNodeIds(new Set())
    notifySelectionChange(new Set())
  }, [notifySelectionChange])

  // Build selected node info for the selection panel
  const selectedNodesInfo: SelectedNodeInfo[] = useMemo(() => {
    const nodes: SelectedNodeInfo[] = []
    for (const id of selectedNodeIds) {
      const kgNode = kgNodeMap.get(id)
      if (kgNode) {
        nodes.push({ id: kgNode.id, label: kgNode.label, caption: kgNode.caption, properties: kgNode.properties })
      }
    }
    return nodes
  }, [selectedNodeIds, kgNodeMap])

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-sm text-gray-500 dark:text-gray-400" style={{ height }}>
        Failed to load graph data. Is Neo4j running?
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 text-sm text-gray-500 dark:text-gray-400" style={{ height }}>
        Loading graph...
      </div>
    )
  }

  if (nvlNodes.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-sm text-gray-500 dark:text-gray-400" style={{ height }}>
        No graph data available. Try running a KG backfill first.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2" style={{ height }}>
      {/* Toolbar */}
      <KgToolbar
        layout={layout}
        onLayoutChange={setLayout}
        visibleTypes={visibleTypes}
        onTypeToggle={handleTypeToggle}
        onSetVisibleTypes={setVisibleTypes}
        availableTypes={availableTypes}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearchSubmit={handleSearch}
        depth={depth}
        onDepthChange={setDepth}
        nodeCount={nvlNodes.length}
        relCount={nvlRels.length}
      />

      {/* Graph + Detail panel */}
      <div className="flex flex-1 min-h-0 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-950">
        <div className="flex-1 relative" ref={graphContainerRef}>
          {layout === 'radial' ? (
            <KgXyflowView
              kgNodes={filteredKgNodes}
              kgRels={filteredKgRels}
              selectedNodeIds={selectedNodeIds}
              onNodeClick={(nodeId, shiftKey) => {
                if (shiftKey) {
                  setSelectedNodeIds(prev => {
                    const next = new Set(prev)
                    if (next.has(nodeId)) next.delete(nodeId)
                    else next.add(nodeId)
                    notifySelectionChange(next)
                    return next
                  })
                } else {
                  setSelectedNodeIds(prev => {
                    if (prev.size === 1 && prev.has(nodeId)) {
                      notifySelectionChange(new Set())
                      return new Set()
                    }
                    const next = new Set([nodeId])
                    notifySelectionChange(next)
                    return next
                  })
                }
              }}
              onNodeDoubleClick={async (nodeId) => {
                try {
                  const result = await api.kg.browser.expand(nodeId, 1)
                  setExtraNodes(prev => [...prev, ...result.nodes])
                  setExtraRels(prev => [...prev, ...result.relationships])
                } catch (err) {
                  console.error('[KG] Expand failed:', err)
                }
              }}
              onCanvasClick={() => {
                handleClearSelection()
                setTooltip(prev => ({ ...prev, visible: false }))
              }}
            />
          ) : (
            <>
              <InteractiveNvlWrapper
                ref={nvlRef}
                nodes={nvlNodes}
                rels={nvlRels}
                layout={layout}
                nvlOptions={{
                  allowDynamicMinZoom: true,
                  disableWebGL: false,
                  relationshipThreshold: 0.55,
                }}
                mouseEventCallbacks={{
                  // Click: signature is (node, hitTargets, event)
                  onNodeClick: ((node: NvlNode, hitTargets: unknown, event: MouseEvent) => handleNodeClick(node, hitTargets, event)) as any,
                  onNodeDoubleClick: ((node: NvlNode) => handleNodeDoubleClick(node)) as any,
                  onCanvasClick: (() => { handleClearSelection(); setTooltip(prev => ({ ...prev, visible: false })) }) as any,
                  // Enable pan, zoom, and drag with default behavior
                  onPan: true as any,
                  onZoom: true as any,
                  onDrag: true as any,
                  // Hover tooltip
                  onHover: ((element: any, hitTargets: any, event: MouseEvent) => handleHover(element, hitTargets, event)) as any,
                }}
                interactionOptions={{
                  selectOnClick: true,
                } as any}
                style={{ width: '100%', height: '100%' }}
              />

              {/* Hover tooltip */}
              {tooltip.visible && (
                <div
                  className="absolute z-50 pointer-events-none px-3 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs max-w-xs"
                  style={{ left: tooltip.x, top: tooltip.y }}
                >
                  <div className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[10px] mb-0.5">
                    {tooltip.label}
                  </div>
                  <div className="font-medium text-gray-900 dark:text-white text-sm">
                    {tooltip.caption}
                  </div>
                  {Object.keys(tooltip.properties).length > 0 && (
                    <div className="mt-1 pt-1 border-t border-gray-100 dark:border-gray-700 space-y-0.5">
                      {Object.entries(tooltip.properties).slice(0, 4).map(([key, val]) => (
                        <div key={key} className="flex gap-1">
                          <span className="text-gray-400 dark:text-gray-500">{key}:</span>
                          <span className="text-gray-700 dark:text-gray-300 truncate">{String(val)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail / Selection panel */}
        {selectedNodeIds.size === 1 && (
          <KgNodeDetailPanel
            nodeId={Array.from(selectedNodeIds)[0] ?? null}
            onClose={handleClearSelection}
            onNodeSelect={handleNodeSelectFromPanel}
          />
        )}
        {selectedNodeIds.size > 1 && (
          <KgSelectionPanel
            selectedNodes={selectedNodesInfo}
            onDeselectNode={handleDeselectNode}
            onClearSelection={handleClearSelection}
            onFocusNode={handleNodeSelectFromPanel}
            onAskAboutSelection={onAskAboutSelection}
          />
        )}
      </div>

      {/* Legend */}
      <KgLegend visibleTypes={visibleTypes} />
    </div>
  )
}
